import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { useDirectory } from "@/lib/hooks";
import { Field } from "@/components/PropertyDialog";
import { DateInput } from "@/components/DateInput";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { tenant_id: "", start_date: today(), end_date: "", rent: "", charges: "", deposit: "", currency: "EUR", furnished: false, duration_type: "longue" };

export function LeaseDialog({ unit, trigger }) {
  const { t } = usePrefs();
  const tenants = useDirectory("locataire");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      await api.post("/leases", { ...f, unit_id: unit.id, rent: Number(f.rent), charges: Number(f.charges || 0), deposit: Number(f.deposit || 0) });
      toast.success(t("leaseCreated"));
      qc.invalidateQueries();
      setOpen(false);
      setF(EMPTY);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg rounded-sm" data-testid="lease-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newLease")} — <span className="font-data text-lg">{unit.reference}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Field label={t("tenant")}>
              <Select value={f.tenant_id} onValueChange={set("tenant_id")}>
                <SelectTrigger data-testid="lease-tenant-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{tenants.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t("start")}><DateInput value={f.start_date} onChange={set("start_date")} data-testid="lease-start-input" /></Field>
          <Field label={t("end")}><DateInput value={f.end_date} onChange={set("end_date")} data-testid="lease-end-input" /></Field>
          <Field label={t("rent")}><Input type="number" value={f.rent} onChange={set("rent")} data-testid="lease-rent-input" /></Field>
          <Field label={t("charges")}><Input type="number" value={f.charges} onChange={set("charges")} data-testid="lease-charges-input" /></Field>
          <Field label={t("deposit")}><Input type="number" value={f.deposit} onChange={set("deposit")} data-testid="lease-deposit-input" /></Field>
          <Field label={t("currency")}>
            <Select value={f.currency} onValueChange={set("currency")}>
              <SelectTrigger data-testid="lease-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label={t("duration")}>
            <Select value={f.duration_type} onValueChange={set("duration_type")}>
              <SelectTrigger data-testid="lease-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="longue">{t("long")}</SelectItem><SelectItem value="courte">{t("short")}</SelectItem></SelectContent>
            </Select>
          </Field>
          <label className="flex items-end gap-2 pb-2 text-sm"><Checkbox checked={f.furnished} onCheckedChange={(v) => setF({ ...f, furnished: !!v })} data-testid="lease-furnished-checkbox" />{t("furnished")}</label>
        </div>
        <Button onClick={save} disabled={!f.tenant_id || !f.end_date || !f.rent} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="lease-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}
